import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Button } from "@/components/ui/button";
import { ScanEye, ArrowRight, Lock, Loader2, GitCompareArrows } from "lucide-react";

// Pre-Move-Out Self-Audit entry card — lives in the move-out tab beside Proof Replay.
// FREE users see the description + upgrade; existing saved results stay viewable after downgrade.
export function SelfAuditCard({ propertyId }) {
  const navigate = useNavigate();
  const { isPremium, trialing, trialEligible, openUpgrade } = useTier();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    api.get(`/properties/${propertyId}/self-audit`)
      .then((r) => ok && setD(r.data))
      .catch(() => ok && setD(false))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [propertyId]);

  if (loading) return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 flex items-center justify-center theme-card" data-testid="self-audit-card-loading">
      <Loader2 className="animate-spin text-brand" size={18} />
    </div>
  );
  if (!d) return null;

  const c = d.counts || {};
  const canRun = isPremium || trialing;
  const hasSaved = (d.pairs || []).some((p) => p.outcome || (p.confirmed_observations || []).length > 0);
  const cta = getPlanCta({ trialEligible, trialing });

  // Empty state: no confirmed Proof Replay pairs yet.
  if (d.empty) {
    return (
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card" data-testid="self-audit-card-empty">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><ScanEye size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy">Pre-Move-Out Self-Audit</h3>
            <p className="mt-0.5 text-sm text-[#64748B]">{d.empty_reason}</p>
            <Button data-testid="self-audit-card-empty-cta" variant="outline" onClick={() => navigate(`/property/${propertyId}?tab=moveout`)}
              className="mt-3 h-10 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">
              <GitCompareArrows size={15} className="mr-1.5" /> Pair photos with Proof Replay
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Free user with nothing saved → locked description + upgrade.
  if (!canRun && !hasSaved) {
    return (
      <div className="rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 theme-card" data-testid="self-audit-card-locked">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><ScanEye size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">Pre-Move-Out Self-Audit <Lock size={14} className="text-[#94A3B8]" /></h3>
            <p className="mt-0.5 text-sm text-[#64748B]">AI-assisted review of possible visual differences between your paired move-in and move-out photos — before you seal.</p>
            <Button data-testid="self-audit-card-upgrade" onClick={() => openUpgrade("self_audit")}
              className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
              <Lock size={15} className="mr-2" /> {cta.cta}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 theme-card" data-testid="self-audit-card">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/10 text-brand shrink-0"><ScanEye size={20} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg font-bold text-navy">Pre-Move-Out Self-Audit</h3>
          <p className="mt-0.5 text-sm text-[#64748B] leading-snug">Review possible visual differences between paired photos before sealing your move-out evidence.</p>
          <p className="mt-2 text-sm font-semibold text-navy" data-testid="self-audit-card-progress">
            Reviewed {c.reviewed} of {c.confirmed_pairs} confirmed pairs
          </p>
          <p className="text-xs text-[#475569]">{c.awaiting_review} awaiting review{c.retakes_recommended ? ` · ${c.retakes_recommended} retake recommended` : ""}</p>
          <Button data-testid="self-audit-card-open" onClick={() => navigate(`/self-audit/${propertyId}`)}
            className="mt-3 h-10 rounded-xl bg-navy hover:bg-navy-hover text-white">
            {c.reviewed > 0 ? "Continue self-audit" : "Start self-audit"} <ArrowRight size={15} className="ml-1.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
